// A new currency also needs the `BookingEventTrainerChanges` currency CHECKs
// (`booking_event_trainer_changes_{previous,new}_currency_chk`, migration
// 20261010110000-booking-trainer-fee-and-changes) widened by a new migration,
// or logging a fee change in it fails (MV2-577).
export enum Currency {
  RSD = 'RSD',
  EUR = 'EUR',
  USD = 'USD',
  BAM = 'BAM',
}

export enum PaymentProvider {
  STRIPE = 'stripe',
  PAYSPOT = 'payspot',
  // fictitious/offline payment (cash, payment slip…) — recorded without a processor;
  // used by membership until it switches to online billing
  MANUAL = 'manual',
}

export enum PayerType {
  USER = 'user',
  ORGANIZATION = 'organization',
}

export enum InstrumentOwnerType {
  USER = 'user',
  ORGANIZATION = 'organization',
}

export enum InstrumentStatus {
  ACTIVE = 'active',
  EXPIRED = 'expired',
  REMOVED = 'removed',
}

export enum SubscriptionSource {
  PLATFORM_BILLING = 'platform_billing',
  MEMBERSHIP = 'membership',
  MARKETPLACE = 'marketplace',
  PREMIUM_CONTENT = 'premium_content',
  // F9.2 — premium stats subscription (9.99 €/mo for everyone; no free tier)
  PREMIUM_REPORT = 'premium_report',
}

export enum SubscriptionStatus {
  TRIALING = 'trialing',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  GRACE = 'grace',
  SUSPENDED = 'suspended',
  PAUSED = 'paused',
  CANCELED = 'canceled',
  INCOMPLETE = 'incomplete',
}

export enum BillingInterval {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export enum PricingMode {
  FIXED = 'fixed',
  PER_SEAT = 'per_seat',
  METERED = 'metered',
  FLAT = 'flat',
}

export enum BillingPackage {
  STANDARD = 'standard',
  REBRAND = 'rebrand',
  CUSTOM = 'custom',
  STAIRS = 'stairs',
}

export enum OrgBillingStatus {
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  GRACE = 'grace',
  // the organization owes money and the dunning/grace window ran out — LOCKED,
  // and the only way out is paying
  SUSPENDED = 'suspended',
  // Q3/Task 3: the plan was CANCELLED (by its owner, or by closing the
  // organization) and the paid period has run out. Also LOCKED, but the
  // organization owes NOTHING — it simply has no subscription any more, and
  // choosing a package again revives it. Deliberately NOT `SUSPENDED`: every
  // surface that explains the lock reads "unpaid balance" off that value, and
  // calling a paid-up customer a debtor is the one thing this state exists to
  // prevent. `OrgBillingLockReason` is how the two are told apart.
  ENDED = 'ended',
}

// Machine-readable API error codes for the finance layer (FE branches behavior
// by code, not by the translated message)
export enum PaymentApiErrorCode {
  // org billing lock (§16, R-AUD-62): org suspended due to unpaid billing
  ORG_BILLING_LOCKED = 'ORG_BILLING_LOCKED',
  // 6.8: the platform denied the seller marketplace access — publishing is
  // refused until it is restored
  MARKETPLACE_ACCESS_DENIED = 'MARKETPLACE_ACCESS_DENIED',
  // premium/premium_report subscribe without a saved MIT-eligible card —
  // FE reacts by starting the card-setup-then-purchase chain (2-in-1)
  NO_SAVED_CARD = 'NO_SAVED_CARD',
  // Membership activation must collect consent before starting card setup.
  MEMBERSHIP_TOU_REQUIRED = 'MEMBERSHIP_TOU_REQUIRED',
  MEMBERSHIP_TOU_UNAVAILABLE = 'MEMBERSHIP_TOU_UNAVAILABLE',
  // Live test 2/3 (N1, Rulings AG/AH): the cycle can no longer be charged by
  // card — ended early, replaced, not started or over (`cardActivationBlocker`)
  MEMBERSHIP_CYCLE_NOT_CHARGEABLE = 'MEMBERSHIP_CYCLE_NOT_CHARGEABLE',
  // T44-1/T44-2: a card-now cycle running on trust that the desk already took
  // money for — card billing would bill it a second time
  // (`cardTakeoverPaidByHand`)
  MEMBERSHIP_CYCLE_PAID_BY_HAND = 'MEMBERSHIP_CYCLE_PAID_BY_HAND',
  // adding an active client over the package's seat cap
  SEAT_CAP_REACHED = 'SEAT_CAP_REACHED',
  // choosing a plan while the organization's platform subscription still owes
  // an OPEN invoice (typically: the org was closed with a debt, the debt never
  // cleared, and it is now being reactivated). The FE reacts by offering
  // pay-now — the debt has to be settled before a package can be chosen.
  OUTSTANDING_INVOICE = 'OUTSTANDING_INVOICE',
  // Q3/Task 3: a cancel/withdraw-cancel request arrived for an organization
  // whose platform subscription has ALREADY ended (the runner cancelled it at
  // the period end). There is nothing left to cancel or to withdraw — the FE
  // reacts by offering the package catalog, which is the real next step.
  SUBSCRIPTION_ALREADY_ENDED = 'SUBSCRIPTION_ALREADY_ENDED',
  // Q3/Task 3: withdraw-cancel arrived on a subscription that is not merely
  // cancelling but SETTLING A CLOSURE (Q4) — its period was cut to the closing
  // instant and the plan carries the proration marker that prices that final
  // invoice. Clearing the flag would restore half a subscription against
  // bounds that no longer mean anything, so the withdrawal is refused: coming
  // back from a closure is choosing a plan again, not un-cancelling.
  CLOSURE_SETTLEMENT_PENDING = 'CLOSURE_SETTLEMENT_PENDING',
  // H3: a DIFFERENT package was chosen while the subscription is set to end at
  // its period end (`cancelAtPeriodEnd`). Every package-change branch rewrites
  // the period or the next charge date, and the runner then honours the armed
  // cancellation against THAT — which ended the organization within a minute
  // and destroyed the unused prepaid remainder it had just been credited. The
  // change is refused instead. For an owner cancellation the FE offers "Keep
  // subscription" (withdraw), after which the change goes through; a closure
  // (`org_closed`) cannot be withdrawn and simply runs out its paid period.
  CANCELLATION_PENDING = 'CANCELLATION_PENDING',
  // Q3/Task 3: a cancel/withdraw-cancel compare-and-set did not hold because
  // the subscription's STATUS moved while the owner was deciding (a renewal
  // failed into past_due, a grace was granted, a pay-now succeeded) — it is
  // still very much alive. Retryable: reload and try again. Deliberately NOT
  // `SUBSCRIPTION_ALREADY_ENDED`, which would tell an owner in dunning that
  // their subscription is gone and point them at a purchase they do not need.
  SUBSCRIPTION_STATE_CHANGED = 'SUBSCRIPTION_STATE_CHANGED',
  // Q8/Task 6: a premium interval switch was requested for the interval the
  // subscription is ALREADY on, with nothing scheduled to undo. The same
  // request WITH a pending switch is the undo and succeeds, so this code only
  // ever means "there is nothing to change here" — the FE keeps the card as it
  // is instead of reporting a failure the person cannot act on.
  PREMIUM_INTERVAL_UNCHANGED = 'PREMIUM_INTERVAL_UNCHANGED',
  // Q6/Task 11: a monthly payout statement was requested for a month that has
  // not ENDED yet (in the platform timezone). An issued statement never
  // changes, so it can only state a month whose payouts have stopped arriving —
  // the current month has no settled truth to state. The FE reacts by offering
  // the last CLOSED month instead of reporting a failure the owner cannot act
  // on; the same request one month later succeeds unchanged.
  STATEMENT_PERIOD_NOT_CLOSED = 'STATEMENT_PERIOD_NOT_CLOSED',
  // Q6/Task 11 (Ruling R39): a payout statement was requested for a month that
  // ENDED BEFORE the organization existed. Every month since the year dot is a
  // "closed" month, so without this bound an authorised member could mint tens
  // of thousands of immutable records of months in which there was nothing to
  // record. The FE reacts by offering the organization's own first month as the
  // earliest choice.
  STATEMENT_PERIOD_BEFORE_ORGANIZATION = 'STATEMENT_PERIOD_BEFORE_ORGANIZATION',
  // Task 7.1 (V2-28): the two 409s of the organization's Platform Analytics
  // subscribe — the launch switch (PLATFORM_ANALYTICS_ENABLED) is off, or the
  // organization already has a live subscription (uq_sub_premium_report_per_org).
  PLATFORM_ANALYTICS_UNAVAILABLE = 'PLATFORM_ANALYTICS_UNAVAILABLE',
  PLATFORM_ANALYTICS_ALREADY_SUBSCRIBED = 'PLATFORM_ANALYTICS_ALREADY_SUBSCRIBED',
  // Pricing v2 (D11): a stair term's nominal discount plus the organization's
  // platform discounts would exceed MAX_COMBINED_DISCOUNT_PERCENT — the term
  // choice, the discount grant and the offline import refuse it alike
  COMBINED_DISCOUNT_TOO_HIGH = 'COMBINED_DISCOUNT_TOO_HIGH',
}

/**
 * The user-facing sentence for `ORG_BILLING_LOCKED`, shared by every thrower
 * (the route guard's `lockedException` and the marketplace publish hook). One
 * constant because axios drops the `code` off a 403 response body, so this
 * sentence is the only field a user ever sees — two hand-copied spellings
 * would drift silently, and nothing else pins them together.
 */
export const ORG_BILLING_LOCKED_MESSAGE =
  'Organization is temporarily unavailable due to an unpaid balance';

/**
 * The same sentence, for the OTHER locked state (Q3/Task 3): the organization
 * cancelled its plan and is paid up. Same rules as the constant above — one
 * spelling, shared by every thrower, because axios drops the `code` and this
 * text is all a user sees.
 *
 * It exists because reusing `ORG_BILLING_LOCKED_MESSAGE` here would tell a
 * customer who owes nothing that they have an unpaid balance. The two are
 * pinned apart by `payments/__tests__/org-billing-lock-message.spec.ts`.
 */
export const ORG_SUBSCRIPTION_ENDED_MESSAGE =
  'Organization is unavailable because its subscription has ended';

export enum OnboardingStatus {
  NONE = 'none',
  REQUESTED = 'requested',
  CONTRACT_PENDING = 'contract_pending',
  ACCOUNT_VALIDATION = 'account_validation',
  ACTIVE = 'active',
  REJECTED = 'rejected',
  SUSPENDED = 'suspended',
}

export enum PaymentMethod {
  CARD = 'card',
  IPS = 'ips',
  EMONEY = 'emoney',
  TRANSFER = 'transfer',
  WALLET = 'wallet',
  // manual payments outside the processor — cash is the dominant method in gyms
  CASH = 'cash',
  OTHER = 'other',
}

// item kind in the platform package catalog (PlatformPackages — pricing lives in
// the DB, not in code): per_seat = package whose prices are carried by PricingScale;
// rebrand = standalone rebrand subscription/lifetime; bundle = FLAT package (cap + period)
export enum PlatformPackageKind {
  PER_SEAT = 'per_seat',
  REBRAND = 'rebrand',
  BUNDLE = 'bundle',
  STAIR = 'stair',
}

export enum NormalizedStatus {
  CREATED = 'created',
  AUTHORIZED = 'authorized',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  VOIDED = 'voided',
  REFUNDED = 'refunded',
  PARTIALLY_REFUNDED = 'partially_refunded',
  DISPUTED = 'disputed',
  EXPIRED = 'expired',
}

export enum InvoiceStatus {
  DRAFT = 'draft',
  OPEN = 'open',
  PAID = 'paid',
  VOID = 'void',
  UNCOLLECTIBLE = 'uncollectible',
}

export enum InvoiceLineType {
  PER_SEAT = 'per_seat',
  FIXED_FEE = 'fixed_fee',
  REBRAND_FEE = 'rebrand_fee',
  FLAT = 'flat',
  DISCOUNT = 'discount',
  ITEM = 'item',
  ADJUSTMENT = 'adjustment',
}

export enum ReconciliationItemType {
  PAYMENT = 'payment',
  PAYOUT = 'payout',
}

export enum ReconciliationItemStatus {
  PENDING = 'pending',
  DONE = 'done',
  EXHAUSTED = 'exhausted',
}

export enum PaymentTransactionKind {
  CHARGE = 'charge',
  REFUND = 'refund',
  DISPUTE = 'dispute',
  ADJUSTMENT = 'adjustment',
}

export enum MarketplaceFulfillmentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  DELIVERED = 'delivered',
  RETRYABLE = 'retryable',
  UNAVAILABLE = 'unavailable',
  REFUND_PENDING = 'refund_pending',
  REFUND_PROCESSING = 'refund_processing',
  REFUND_RETRYABLE = 'refund_retryable',
  REFUNDED = 'refunded',
  REFUND_FAILED = 'refund_failed',
}

export enum PayoutStatus {
  // created (PaySpot: MT101 sent), awaiting the buyer's payment
  PENDING = 'pending',
  // payment confirmed — the hold window is running (collect → hold → disburse)
  HOLDING = 'holding',
  // payout confirmed (PaySpot MT110 / Stripe Transfer) — awaiting T+1 settlement
  CONFIRMED = 'confirmed',
  PAID = 'paid',
  FAILED = 'failed',
  // canceled (abandoned purchase / refund before payout)
  VOIDED = 'voided',
  // abandoned-sweep claim while the remote MT105 cancellation is in flight —
  // blocks the paid transition until the remote outcome is known
  CANCELING = 'canceling',
  // remote cancellation gave up after the retry budget: the PaySpot group may
  // still be payable — manual review; a late payment resumes the paid path
  CANCEL_FAILED = 'cancel_failed',
}

export enum LedgerAccount {
  // dedicated/escrow account (buyers' money)
  ESCROW = 'escrow',
  // liability to the author (net amount owed)
  BENEFICIARY_PAYABLE = 'beneficiary_payable',
  // our commission (revenue)
  PLATFORM_REVENUE = 'platform_revenue',
  // processor commission (expense)
  PROVIDER_FEE = 'provider_fee',
  // refunds in progress
  REFUND_CLEARING = 'refund_clearing',
}

/** What business change a ledger posting group records. Every row of one
 *  `entryGroup` carries the same kind. */
export enum LedgerEntryKind {
  /** buyer paid: escrow ← gross, owed to seller / platform / processor */
  PURCHASE = 'purchase',
  /** full refund before payout: the purchase group mirrored */
  PURCHASE_REVERSAL = 'purchase_reversal',
  /** seller paid out: payable cleared against escrow */
  DISBURSEMENT = 'disbursement',
}

export enum CouponDuration {
  ONCE = 'once',
  REPEATING = 'repeating',
  FOREVER = 'forever',
}

export enum OrgSeatEventType {
  ADDED = 'added',
  REMOVED = 'removed',
  REACTIVATED = 'reactivated',
  DEACTIVATED = 'deactivated',
}

export enum BillingChangeReason {
  REBRAND_PURCHASED = 'rebrand_purchased',
  REBRAND_CANCELED = 'rebrand_canceled',
  PACKAGE_CHANGED = 'package_changed',
  FLAT_SWITCHED = 'flat_switched',
  SCALE_CHANGED = 'scale_changed',
  CAP_CHANGED = 'cap_changed',
  FIXEDFEE_CHANGED = 'fixedfee_changed',
  STAIR_CHANGED = 'stair_changed',
  TERM_CHANGED = 'term_changed',
  OFFLINE_PREPAID = 'offline_prepaid',
}

export enum RebrandSource {
  STANDALONE = 'standalone',
  BUNDLED = 'bundled',
}

export enum OrgDiscountStatus {
  ACTIVE = 'active',
  EXPIRED = 'expired',
  REVOKED = 'revoked',
}

export enum DiscountScope {
  PER_SEAT = 'per_seat',
  TOTAL = 'total',
}

/**
 * Task 7.2 — WHAT an `OrgDiscounts` row discounts. `platform_billing` = the
 * organization's platform plan (every row before 7.2); `premium_report` = the
 * "Free analytics" switch: 100 % off MyFitWorld Platform Analytics for one
 * month, which also grants access without a subscription.
 */
export enum OrgDiscountAppliesTo {
  PLATFORM_BILLING = 'platform_billing',
  PREMIUM_REPORT = 'premium_report',
}
